import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { readCache, writeCache, cachedGet, withCatImages, useCacheUpdated } from "@/lib/swr";
import { useAutoReload } from "@/lib/useAutoReload";
import { precacheMedia, collectBundleMedia } from "@/lib/mediaCache";
import { Icon } from "@/components/Icon";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RatingButton } from "@/components/RatingButton";
import { ArrowLeft, MapPin, Sparkles, Megaphone, Briefcase, Newspaper, Crown, Moon, Stars, X, Heart, Cake, PartyPopper, Sun, Snowflake, Flower2, Flag, GraduationCap, Trophy, Tag, Gift, BookOpen, Plus, LayoutGrid, ChevronDown, Phone } from "lucide-react";
import Stories from "@/components/Stories";
import DiscountedProductsCarousel from "@/components/DiscountedProductsCarousel";
import FeaturedShopsCarousel from "@/components/FeaturedShopsCarousel";
import EnablePushBanner from "@/components/EnablePushBanner";
// BreakingNews ticker was removed — replaced by the Discounts/Promos section below
import FreeDeliveryDialog from "@/components/FreeDeliveryDialog";
import QuickServicesStrip from "@/components/QuickServicesStrip";
import TaxiRequestSheet from "@/components/TaxiRequestSheet";
import { formatDate } from "@/lib/dateFmt";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { catName, shopName, shopDescription } from "@/i18n/dynamic";
import FavoriteButton from "@/components/FavoriteButton";

const HERO_IMG = null;

const BANNER_THEMES = {
  default:        { bg: "from-primary to-primary/80",                    text: "text-white",      icon: Megaphone },
  // Islamic religious occasions
  eid_fitr:       { bg: "from-emerald-600 to-teal-500",                  text: "text-white",      icon: Stars },
  eid_adha:       { bg: "from-amber-600 via-orange-500 to-red-500",      text: "text-white",      icon: Crown },
  ramadan:        { bg: "from-indigo-700 to-purple-600",                 text: "text-amber-100",  icon: Moon },
  muharram:       { bg: "from-slate-800 to-slate-700",                   text: "text-amber-100",  icon: Moon },
  ashura:         { bg: "from-rose-900 via-red-900 to-slate-900",        text: "text-amber-100",  icon: Moon },
  arbaeen:        { bg: "from-slate-800 via-rose-900 to-slate-900",      text: "text-amber-100",  icon: Heart },
  mawlid:         { bg: "from-emerald-700 via-green-600 to-teal-600",    text: "text-white",      icon: Sparkles },
  hajj:           { bg: "from-amber-700 via-yellow-600 to-amber-500",    text: "text-white",      icon: Crown },
  new_year_hijri: { bg: "from-blue-800 to-indigo-700",                   text: "text-amber-100",  icon: Sparkles },
  friday:         { bg: "from-teal-600 to-emerald-500",                  text: "text-white",      icon: BookOpen },
  // National (Iraqi)
  independence:   { bg: "from-red-600 via-white to-emerald-600",         text: "text-slate-900",  icon: Flag },
  iraqi_army:     { bg: "from-emerald-900 via-emerald-800 to-amber-700", text: "text-amber-100",  icon: Trophy },
  newroz:         { bg: "from-rose-600 via-yellow-500 to-emerald-600",   text: "text-white",      icon: Flower2 },
  // Social & seasonal
  new_year:       { bg: "from-fuchsia-700 via-purple-600 to-blue-600",   text: "text-white",      icon: PartyPopper },
  mothers_day:    { bg: "from-pink-500 via-rose-400 to-pink-300",        text: "text-white",      icon: Heart },
  teachers_day:   { bg: "from-blue-600 via-cyan-500 to-sky-400",         text: "text-white",      icon: GraduationCap },
  birthday:       { bg: "from-pink-500 via-purple-500 to-indigo-500",    text: "text-white",      icon: Cake },
  wedding:        { bg: "from-rose-400 via-pink-400 to-fuchsia-400",     text: "text-white",      icon: Gift },
  summer:         { bg: "from-sky-500 via-cyan-400 to-amber-300",        text: "text-white",      icon: Sun },
  winter:         { bg: "from-slate-500 via-blue-500 to-cyan-400",       text: "text-white",      icon: Snowflake },
  // Commerce
  sale:           { bg: "from-orange-600 via-amber-500 to-yellow-400",   text: "text-white",      icon: Tag },
  announcement:   { bg: "from-slate-700 via-slate-600 to-blue-700",      text: "text-white",      icon: Megaphone },
  // Backwards-compat alias
  eid:            { bg: "from-emerald-600 to-teal-500",                  text: "text-white",      icon: Stars },
};

function isShopOpenNow(opening, closing) {
  // Returns true/false when both times are set, null if unknown
  if (!opening || !closing) return null;
  const m = (s) => {
    const [h, mm] = String(s).split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(mm)) return null;
    return h * 60 + mm;
  };
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const openMin = m(opening);
  const closeMin = m(closing);
  if (openMin == null || closeMin == null) return null;
  // Overnight (e.g. 22:00 - 02:00)
  if (closeMin <= openMin) return nowMin >= openMin || nowMin < closeMin;
  return nowMin >= openMin && nowMin < closeMin;
}

// Horizontal strip that auto-scrolls to the right every ~30 ms, loops back to
// the start when it reaches the end, and pauses when the user is interacting
// with it (touch/scroll/hover).
function AutoSlideStrip({ children }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let paused = false, raf = 0, last = performance.now();
    const PAUSE_MS = 2200;
    let pauseUntil = 0;
    const tick = (now) => {
      raf = requestAnimationFrame(tick);
      if (paused || now < pauseUntil) { last = now; return; }
      const dt = Math.min(now - last, 40); last = now;
      const dir = document.documentElement.dir === "rtl" ? -1 : 1;
      el.scrollLeft += dir * 0.6 * (dt / 16);  // ~37 px/s
      const max = el.scrollWidth - el.clientWidth;
      if (dir > 0 && el.scrollLeft >= max - 1) el.scrollLeft = 0;
      else if (dir < 0 && el.scrollLeft <= 0) el.scrollLeft = max;
    };
    const nudge = () => { pauseUntil = performance.now() + PAUSE_MS; };
    const stop = () => { paused = true; };
    const resume = () => { paused = false; };
    el.addEventListener("touchstart", stop, { passive: true });
    el.addEventListener("touchend", () => { nudge(); resume(); }, { passive: true });
    el.addEventListener("mouseenter", stop);
    el.addEventListener("mouseleave", resume);
    el.addEventListener("wheel", nudge, { passive: true });
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  return (
    <div ref={ref} className="kh-scroll-x flex gap-3 overflow-x-auto pb-3 -mx-4 px-4 snap-x">
      {children}
    </div>
  );
}

function ShopStripCard({ s, cat, lang, testId }) {
  const open = isShopOpenNow(s.opening_time, s.closing_time);
  const isClosed = open === false;
  const prefetch = () => {
    api.get(`/shops/${s.id}/bundle`).then(r => {
      writeCache(`shop-bundle:${s.id}`, r.data);
      precacheMedia(collectBundleMedia(r.data));
    }).catch(() => {});
  };
  return (
    <Link
      to={`/shop/${s.id}`}
      state={{ shop: s }}
      onMouseEnter={prefetch}
      onTouchStart={prefetch}
      onFocus={prefetch}
      className="card-lift shrink-0 w-[160px] sm:w-[180px] snap-start rounded-2xl overflow-hidden surface-elevated relative"
      data-testid={testId}
    >
      <div className="relative w-full h-[110px] overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900">
        {s.cover_image ? (
          <img src={s.cover_image} alt={s.name} className={`w-full h-full object-cover transition ${isClosed ? "grayscale opacity-60" : ""}`} loading="lazy" decoding="async" />
        ) : cat?.image ? (
          <img src={cat.image} alt={s.name} className={`w-full h-full object-contain p-2 scale-110 drop-shadow-[0_4px_8px_rgba(0,0,0,0.35)] ${isClosed ? "grayscale opacity-60" : ""}`} loading="lazy" decoding="async" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon name={cat?.icon || "Store"} className="size-10 text-primary/60" />
          </div>
        )}

        {/* Closed shop dark overlay + hours label */}
        {isClosed && (
          <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-[1px] flex flex-col items-center justify-center text-center px-2 pointer-events-none">
            <div className="px-2 py-0.5 rounded-full bg-slate-900/90 text-white text-[10px] font-black shadow border border-white/20">
              مغلق
            </div>
            {s.opening_time && s.closing_time && (
              <div className="mt-1 text-[9px] text-white/80 font-bold leading-tight">
                يفتح {s.opening_time} - {s.closing_time}
              </div>
            )}
          </div>
        )}

        {/* Open badge (only when open) */}
        {open === true && (
          <div className="absolute top-1.5 start-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-primary/95 text-primary-foreground shadow">
            مفتوح
          </div>
        )}
        {/* Favorite heart */}
        <div className="absolute top-1.5 end-1.5">
          <FavoriteButton shopId={s.id} size="sm" />
        </div>
        {/* Featured/gold star */}
        {s.is_featured && !isClosed && (
          <div className="absolute bottom-1.5 end-1.5 size-6 rounded-full bg-amber-400 text-slate-900 flex items-center justify-center text-[11px] font-black shadow" title="مميّز">
            ★
          </div>
        )}
      </div>
      <div className="p-2.5">
        <div className="font-bold text-[13px] line-clamp-1 leading-tight">{lang === 'ku' && s.name_ku ? s.name_ku : (lang === 'en' && s.name_en ? s.name_en : s.name)}</div>
        <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1 line-clamp-1">
          <MapPin className="size-2.5 shrink-0" />
          <span className="line-clamp-1">{s.address || "هود"}</span>
        </div>
      </div>
    </Link>
  );
}

function HomeBanners({ items, onDismiss }) {
  if (!items?.length) return null;
  return (
    <div className="bg-background border-b">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-2 space-y-2">
        {items.map(b => {
          const theme = BANNER_THEMES[b.theme] || BANNER_THEMES.default;
          const Ico = theme.icon;
          return (
            <div
              key={b.id}
              data-testid={`home-banner-${b.id}`}
              className={`relative rounded-2xl px-4 py-3 sm:px-6 sm:py-4 bg-gradient-to-bl ${theme.bg} ${theme.text} shadow-md overflow-hidden`}
            >
              <div className="absolute -end-8 -top-8 opacity-20">
                <Ico className="size-32" />
              </div>
              <div className="relative flex items-center gap-3 sm:gap-4">
                <div className="size-10 sm:size-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                  <Ico className="size-5 sm:size-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-base sm:text-lg leading-tight">{b.title}</div>
                  {b.message && <div className="text-xs sm:text-sm opacity-95 mt-0.5 line-clamp-2">{b.message}</div>}
                </div>
                <button
                  onClick={() => onDismiss(b.id)}
                  data-testid={`dismiss-banner-${b.id}`}
                  className="size-8 rounded-full hover:bg-white/20 flex items-center justify-center shrink-0 transition"
                  aria-label="إغلاق"
                >
                  <X className="size-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Home() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const homeC = readCache("home-init") || {};
  const [cats, setCats] = useState(() => withCatImages(homeC.categories));
  const [shops, setShops] = useState(() => homeC.shops || []);
  const [promos, setPromos] = useState(() => (homeC.promotions || []).slice(0, 8));
  const [discountedProducts, setDiscountedProducts] = useState([]);
  const [news, setNews] = useState(() => (homeC.news || []).slice(0, 10));
  const [featured, setFeatured] = useState(() => homeC.featured || []);
  const [banners, setBanners] = useState([]);
  const [freeDeliveryBanner, setFreeDeliveryBanner] = useState("");

  useEffect(() => {
    cachedGet("free-delivery-banner", "/settings/free-delivery-banner", d => setFreeDeliveryBanner(d?.image || ""));
    cachedGet("discounted-products", "/products/discounted?limit=12", d => setDiscountedProducts(d || []));
  }, []);

  const applyHome = (d) => {
    setCats(withCatImages(d.categories));
    setShops(d.shops || []);
    setFeatured(d.featured || []);
    setPromos((d.promotions || []).slice(0, 8));
    setNews((d.news || []).slice(0, 10));
  };

  const loadHome = () => {
    api.get("/home-init").then(r => {
      const d = r.data || {};
      writeCache("home-init", d);
      applyHome(d);
      const urls = [];
      (d.shops || []).forEach(s => { if (s.logo) urls.push(s.logo); if (s.cover_image) urls.push(s.cover_image); });
      (d.categories || []).forEach(c => c.image && urls.push(c.image));
      (d.promotions || []).forEach(p => p.image && urls.push(p.image));
      precacheMedia(urls);
      api.get("/categories/images").then(res => {
        writeCache("categories-images", res.data || {});
        setCats(withCatImages(d.categories));
      }).catch(() => {});
    }).catch(() => {
      if (readCache("home-init")) return;
      api.get("/categories?with_images=false").then(r => setCats(r.data)).catch(() => {});
      api.get("/shops").then(r => setShops(r.data)).catch(() => {});
      api.get("/promotions").then(r => setPromos(r.data.slice(0, 8))).catch(() => {});
      api.get("/news").then(r => setNews(r.data.slice(0, 10))).catch(() => {});
      api.get("/shops/featured").then(r => setFeatured(r.data)).catch(() => {});
    });
  };

  useEffect(() => {
    loadHome();
    // Banners are user-specific (dismissed state) — keep separate
    cachedGet("home-banners", "/banners", d => {
      const dismissed = JSON.parse(localStorage.getItem("dismissed_banners") || "[]");
      setBanners((d || []).filter(b => !dismissed.includes(b.id)));
    });
  }, []);
  useCacheUpdated(k => k === "home-init" || k === "categories-images", () => applyHome(readCache("home-init") || {}));
  useAutoReload(loadHome);

  const dismissBanner = (id) => {
    const dismissed = JSON.parse(localStorage.getItem("dismissed_banners") || "[]");
    localStorage.setItem("dismissed_banners", JSON.stringify([...dismissed, id]));
    setBanners(prev => prev.filter(b => b.id !== id));
  };

  const topCats = cats.filter(c => !c.parent_slug && c.slug !== "news");
  const [showAllCats, setShowAllCats] = useState(false);
  const [showTaxiInternal, setShowTaxiInternal] = useState(false);
  const [showTaxiExternal, setShowTaxiExternal] = useState(false);
  const [taxiSettings, setTaxiSettings] = useState({});
  useEffect(() => {
    cachedGet("taxi-settings", "/taxi/settings", d => setTaxiSettings(d || {}));
  }, []);

  // Auto-scroll to taxi services when arriving with #taxi-services hash
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash !== "#taxi-services") return;
    // Wait for banners to mount (they render after home-init resolves)
    const tryScroll = (attempt = 0) => {
      const el = document.getElementById("taxi-services");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (attempt < 8) {
        setTimeout(() => tryScroll(attempt + 1), 200);
      }
    };
    tryScroll();
  }, []);
  const [searchQuery, setSearchQuery] = useState("");

  // Build map: top-level slug -> Set of slugs (includes itself + all its subcategories)
  const catSlugsById = (() => {
    const m = {};
    topCats.forEach(tc => {
      const subs = cats.filter(sc => sc.parent_slug === tc.slug).map(sc => sc.slug);
      m[tc.slug] = new Set([tc.slug, ...subs]);
    });
    return m;
  })();

  const searchResults = (() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return null;
    return shops.filter(s => {
      const names = [s.name, s.name_ku, s.name_en, s.description, s.description_ku, s.description_en, s.address, s.specialty]
        .filter(Boolean).map(x => x.toLowerCase());
      return names.some(n => n.includes(q));
    }).slice(0, 24);
  })();

  // Group shops by category slug for per-category horizontal strips
  // Include shops from subcategories under their parent top-level category
  const shopsByCategory = topCats
    .map(c => {
      const slugSet = catSlugsById[c.slug] || new Set([c.slug]);
      const matching = shops.filter(s => slugSet.has(s.category_slug));
      return { cat: c, list: matching, total: matching.length };
    })
    .filter(x => x.list.length > 0);

  const CAT_PALETTES = [
    { bg: "from-amber-100 to-orange-50",   chip: "bg-amber-500/15 text-amber-700",   accent: "text-amber-700",   blob: "bg-amber-300/30",   border: "border-amber-200/60" },
    { bg: "from-rose-100 to-pink-50",      chip: "bg-rose-500/15 text-rose-700",     accent: "text-rose-700",    blob: "bg-rose-300/30",    border: "border-rose-200/60" },
    { bg: "from-emerald-100 to-teal-50",   chip: "bg-emerald-500/15 text-emerald-700", accent: "text-emerald-700", blob: "bg-emerald-300/30", border: "border-emerald-200/60" },
    { bg: "from-sky-100 to-cyan-50",       chip: "bg-sky-500/15 text-sky-700",       accent: "text-sky-700",     blob: "bg-sky-300/30",     border: "border-sky-200/60" },
    { bg: "from-violet-100 to-purple-50",  chip: "bg-violet-500/15 text-violet-700", accent: "text-violet-700",  blob: "bg-violet-300/30",  border: "border-violet-200/60" },
    { bg: "from-orange-100 to-red-50",     chip: "bg-orange-500/15 text-orange-700", accent: "text-orange-700",  blob: "bg-orange-300/30",  border: "border-orange-200/60" },
    { bg: "from-yellow-100 to-amber-50",   chip: "bg-yellow-500/15 text-yellow-800", accent: "text-yellow-800",  blob: "bg-yellow-300/30",  border: "border-yellow-200/60" },
    { bg: "from-lime-100 to-green-50",     chip: "bg-lime-500/15 text-lime-700",     accent: "text-lime-700",    blob: "bg-lime-300/30",    border: "border-lime-200/60" },
  ];

  return (
    <div className="bg-background">
      <HomeBanners items={banners} onDismiss={dismissBanner} />
      <Stories />
      {/* Premium compact Hero — clean solid card with soft LED edge glow */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-4">
        <div
          className="relative rounded-[24px] overflow-hidden isolate p-8 sm:p-10 lg:p-12"
          data-testid="hero-section"
          style={{
            // Solid deep-forest background (no bleeding corner gradient)
            background: "#0B1A14",
            // Soft LED edge: subtle inner emerald ring + outer glow
            boxShadow:
              "inset 0 0 0 1px rgba(29,207,117,0.22), inset 0 0 40px -8px rgba(29,207,117,0.10), 0 12px 40px -16px rgba(29,207,117,0.25)",
          }}
        >
          {/* Content */}
          <div className="relative z-10 text-white fade-up">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-1.5 mb-3 px-3 py-1 rounded-full bg-primary/95 text-primary-foreground text-xs font-bold shadow-lg glow-primary-soft" data-testid="hero-badge">
                <Sparkles className="size-3" /> {t("hero.badge")}
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight mb-2 drop-shadow-lg">
                {t("hero.title.part1")} <span className="text-primary">{t("stats.khanaqin")}</span>
              </h1>
              <p className="text-sm sm:text-base text-white/85 mb-5 leading-relaxed line-clamp-2 max-w-lg">
                {t("hero.description")}
              </p>
              {/* Search bar */}
              <a
                href="#categories"
                className="flex items-center gap-2 rounded-full bg-slate-900/85 border border-white/10 backdrop-blur-md px-4 py-3 hover:border-primary/50 hover:bg-slate-900 transition-all group"
                data-testid="hero-search-bar"
              >
                <div className="size-8 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                  <MapPin className="size-4 text-primary" />
                </div>
                <span className="flex-1 text-sm text-slate-400 group-hover:text-slate-200 transition text-start">
                  {t("hero.cta.browse")}
                </span>
                <ArrowLeft className="size-4 text-primary rtl:rotate-180" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Shop-owner floating access to Business Dashboard */}
      {user?.role === "shop_owner" && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-4 relative z-20" data-testid="business-cta-home">
          <Link
            to="/shop-dashboard"
            className="block rounded-[22px] surface-elevated border border-primary/30 text-foreground p-4 sm:p-5 hover:border-primary/60 transition-all group relative overflow-hidden"
          >
            <div className="pointer-events-none absolute -top-8 -end-8 size-32 rounded-full bg-primary/15 blur-2xl group-hover:bg-primary/25 transition" />
            <div className="relative flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="size-12 rounded-2xl bg-primary/20 border border-primary/40 backdrop-blur-sm flex items-center justify-center shrink-0">
                  <Briefcase className="size-6 text-primary" />
                </div>
                <div>
                  <div className="font-black text-lg">لوحة إدارة أعمالي</div>
                  <div className="text-xs text-muted-foreground">حجوزاتي • طلباتي • منشوراتي</div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 text-sm font-bold text-primary bg-primary/10 rounded-full px-4 py-2 group-hover:bg-primary/20 transition">
                افتح <ArrowLeft className="size-4 rtl:rotate-180" />
              </div>
            </div>
          </Link>
        </section>
      )}

      {/* Global push-enable prompt (any logged-in user) */}
      <EnablePushBanner />

      {/* Discounts & Offers — REAL products with owner-set discounts (not paid promo ads) */}
      {discountedProducts.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-6" data-testid="section-discounts">
          <div className="flex items-end justify-between mb-3">
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-bold text-rose-400 flex items-center gap-1.5 uppercase tracking-wider mb-0.5">
                <Sparkles className="size-3.5" /> عروض المحلات
              </div>
              <h2 className="text-xl sm:text-2xl font-black leading-tight">التخفيضات والعروض</h2>
            </div>
          </div>
          <DiscountedProductsCarousel products={discountedProducts} categoryLabelMap={Object.fromEntries(cats.map(c => [c.slug, c.name_ar || c.name || c.slug]))} />
        </section>
      )}

      {/* Featured Shops with Golden Badge — wide-rectangle carousel (matches discounts style) */}
      {featured.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-10" data-testid="section-gold-shops">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="text-xs font-bold text-amber-400 mb-1.5 flex items-center gap-1.5 uppercase tracking-wider">
                <Crown className="size-3.5 fill-amber-400 text-amber-400" /> {t("section.gold")}
              </div>
              <h2 className="text-2xl sm:text-3xl font-black">{t("section.featured")}</h2>
            </div>
          </div>
          <FeaturedShopsCarousel shops={featured} cats={cats} />
        </section>
      )}

      {/* Categories — horizontal scrollable strip + toggle to view all */}
      <section id="categories" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-12">
        {/* ⚡ Quick services — vibrant 3-card strip (external taxi, internal taxi, free delivery) */}
        <div id="taxi-services" className="scroll-mt-24">
          <QuickServicesStrip
            taxiSettings={taxiSettings}
            onInternalClick={() => setShowTaxiInternal(true)}
            onExternalClick={() => setShowTaxiExternal(true)}
          />
        </div>

        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="text-sm font-semibold text-amber-700 mb-1 flex items-center gap-1 dark:text-amber-400">
              <LayoutGrid className="size-4" /> {t("categories.label")}
            </div>
            <button
              type="button"
              onClick={() => setShowAllCats(v => !v)}
              className="text-2xl sm:text-3xl font-black hover:text-primary transition text-start flex items-center gap-2"
              data-testid="categories-toggle-btn"
            >
              {t("categories.all")}
              <ChevronDown className={`size-6 transition-transform ${showAllCats ? "rotate-180" : ""}`} />
            </button>
          </div>
          <div className="text-xs text-muted-foreground hidden sm:block">{t("categories.drag_hint")}</div>
        </div>

        {!showAllCats && (
          <div className="kh-scroll-x flex gap-3 overflow-x-auto pb-3 -mx-4 px-4 snap-x" data-testid="categories-strip">
            {topCats.map((c) => {
              const active = false;
              const shopCount = shops.filter(s => (catSlugsById[c.slug] || new Set([c.slug])).has(s.category_slug)).length;
              return (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  data-testid={`category-chip-${c.slug}`}
                  className="shrink-0 w-[96px] snap-start flex flex-col items-center gap-1.5 group"
                >
                  <div className={`relative w-[96px] h-[128px] rounded-[24px] overflow-hidden shadow-md shadow-emerald-900/10 transition-transform group-hover:scale-[1.03] group-active:scale-95
                    ${active ? 'ring-2 ring-emerald-600' : ''}`}
                    style={
                      c.image_has_alpha
                        ? { background: 'transparent' } // Transparent icon → no green tile
                        : { background: 'radial-gradient(circle at 50% 35%, #4ade80 0%, #16a34a 55%, #065f46 100%)' }
                    }
                  >
                    {!c.image_has_alpha && (
                      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 15%, rgba(255,255,255,0.25), transparent 55%)' }} />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center">
                      {c.image ? (
                        <img
                          src={c.image}
                          alt={catName(c, lang)}
                          className={
                            c.image_has_alpha
                              ? "w-full h-full object-contain p-2"
                              : "w-full h-full object-contain scale-[1.35] drop-shadow-[0_4px_8px_rgba(0,0,0,0.35)]"
                          }
                          style={{ mixBlendMode: 'normal' }}
                        />
                      ) : (
                        <div className="size-12 rounded-2xl bg-white/25 backdrop-blur-sm flex items-center justify-center shadow-inner">
                          <Icon name={c.icon} className="size-6 text-white" />
                        </div>
                      )}
                    </div>
                    {shopCount > 0 && (
                      <div className="absolute top-1.5 end-1.5 min-w-[20px] h-[20px] px-1 rounded-full bg-white text-emerald-700 text-[10px] font-black flex items-center justify-center shadow">
                        {shopCount}
                      </div>
                    )}
                  </div>
                  <div className="text-center text-[12px] font-black text-foreground line-clamp-2 leading-tight max-w-full min-h-[2.5em]">{catName(c, lang)}</div>
                </Link>
              );
            })}
          </div>
        )}

        {showAllCats && (
          <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-3" data-testid="categories-grid">
            {topCats.map((c) => {
              const shopCount = shops.filter(s => (catSlugsById[c.slug] || new Set([c.slug])).has(s.category_slug)).length;
              return (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  data-testid={`category-link-${c.slug}`}
                  className="flex flex-col items-center gap-1.5 group"
                >
                  <div
                    className="relative w-full aspect-[3/4] rounded-[22px] overflow-hidden shadow-md shadow-emerald-900/10 transition-transform group-hover:scale-[1.03] group-active:scale-95"
                    style={
                      c.image_has_alpha
                        ? { background: 'transparent' }
                        : { background: 'radial-gradient(circle at 50% 35%, #4ade80 0%, #16a34a 55%, #065f46 100%)' }
                    }
                  >
                    {!c.image_has_alpha && (
                      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 15%, rgba(255,255,255,0.25), transparent 55%)' }} />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center">
                      {c.image ? (
                        <img
                          src={c.image}
                          alt={catName(c, lang)}
                          className={
                            c.image_has_alpha
                              ? "w-full h-full object-contain p-2"
                              : "w-full h-full object-contain scale-[1.35] drop-shadow-[0_4px_8px_rgba(0,0,0,0.35)]"
                          }
                        />
                      ) : (
                        <div className="size-12 rounded-2xl bg-white/25 backdrop-blur-sm flex items-center justify-center shadow-inner">
                          <Icon name={c.icon} className="size-6 text-white" />
                        </div>
                      )}
                    </div>
                    {shopCount > 0 && (
                      <div className="absolute top-1.5 end-1.5 min-w-[20px] h-[20px] px-1 rounded-full bg-white text-emerald-700 text-[10px] font-black flex items-center justify-center shadow">
                        {shopCount}
                      </div>
                    )}
                  </div>
                  <div className="text-center text-[12px] font-black text-foreground line-clamp-2 leading-tight max-w-full min-h-[2.5em]">{catName(c, lang)}</div>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      {/* Search for shops (under categories) */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-6" data-testid="section-shop-search">
        <div className="relative">
          <input
            type="search"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder={t("search.placeholder")}
            className="w-full h-12 rounded-full border border-border/60 bg-card ps-12 pe-4 text-base focus:outline-none focus:border-primary/60 focus:ring-2 focus:ring-primary/20 transition text-foreground placeholder:text-muted-foreground"
            data-testid="home-shop-search-input"
          />
          <div className="absolute start-4 top-1/2 -translate-y-1/2 pointer-events-none">
            <svg xmlns="http://www.w3.org/2000/svg" className="size-5 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
          </div>
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground size-8 rounded-full hover:bg-muted flex items-center justify-center"
              data-testid="home-shop-search-clear"
            >
              <X className="size-4" />
            </button>
          )}
        </div>

        {searchResults !== null && (
          <div className="mt-4" data-testid="search-results">
            <div className="text-sm font-semibold text-muted-foreground mb-3">
              {t("search.results")} — {searchResults.length}
            </div>
            {searchResults.length === 0 ? (
              <div className="p-6 rounded-2xl border-2 border-dashed border-muted-foreground/20 text-center text-muted-foreground bg-muted/20">
                {t("search.none")}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {searchResults.map(s => (
                  <Link
                    key={s.id}
                    to={`/shop/${s.id}`}
                    state={{ shop: s }}
                    onTouchStart={() => api.get(`/shops/${s.id}/bundle`).then(r => writeCache(`shop-bundle:${s.id}`, r.data)).catch(() => {})}
                    onMouseEnter={() => api.get(`/shops/${s.id}/bundle`).then(r => writeCache(`shop-bundle:${s.id}`, r.data)).catch(() => {})}
                    data-testid={`search-result-${s.id}`}
                    className="card-lift rounded-[20px] overflow-hidden surface-elevated hover:border-primary/40 transition"
                  >
                    {s.cover_image ? (
                      <img src={s.cover_image} alt={shopName(s, lang)} className="w-full h-28 object-cover" loading="lazy" decoding="async" />
                    ) : (
                      <div className="w-full h-28 bg-gradient-to-br from-primary/10 to-slate-800" />
                    )}
                    <div className="p-3">
                      <div className="font-bold text-sm line-clamp-1">{shopName(s, lang)}</div>
                      {s.address && (
                        <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5 flex items-center gap-1">
                          <MapPin className="size-3" /> {s.address}
                        </div>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {/* Per-category horizontal shop strips */}
      {shopsByCategory.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-10 space-y-8" data-testid="section-shops-by-category">
          {shopsByCategory.map(({ cat, list, total }, catIdx) => {
            const p = CAT_PALETTES[catIdx % CAT_PALETTES.length];
            // Inject promos in the middle of the shop list
            const promosMidIdx = Math.min(1, Math.floor(shopsByCategory.length / 2) - 1);
            const showPromosBelow = discountedProducts.length > 0 && catIdx === Math.max(1, Math.floor(shopsByCategory.length / 2) - 1);
            return (
              <div key={cat.slug}>
                <div data-testid={`shops-strip-${cat.slug}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="size-16 sm:size-20 rounded-3xl overflow-hidden shrink-0 shadow-md shadow-emerald-900/10" style={{ background: 'radial-gradient(circle at 50% 35%, #4ade80 0%, #16a34a 55%, #065f46 100%)' }}>
                        {cat.image ? (
                          <img src={cat.image} alt={catName(cat, lang)} className="w-full h-full object-contain scale-[1.35] drop-shadow-[0_4px_8px_rgba(0,0,0,0.35)]" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Icon name={cat.icon} className="size-8 text-white" />
                          </div>
                        )}
                      </div>
                      <div>
                        <h3 className="font-black text-xl sm:text-2xl">{catName(cat, lang)}</h3>
                        <div className="text-xs text-muted-foreground">{total} {t("categories.n_shops")}</div>
                      </div>
                    </div>
                    <Link
                      to={`/category/${cat.slug}`}
                      className="text-xs sm:text-sm text-emerald-700 font-black hover:underline flex items-center gap-1 dark:text-emerald-400"
                      data-testid={`cat-see-all-${cat.slug}`}
                    >
                      {t("categories.see_all")} <ArrowLeft className="size-3" />
                    </Link>
                  </div>
                  <AutoSlideStrip>
                    {list.map((s) => (
                      <ShopStripCard key={s.id} s={s} cat={cat} lang={lang} testId={`cat-shop-${cat.slug}-${s.id}`} />
                    ))}
                    {/* "+" tile to see all shops in this category */}
                    <Link
                      to={`/category/${cat.slug}`}
                      className="card-lift shrink-0 w-[160px] sm:w-[170px] snap-start surface-elevated border-2 border-dashed border-primary/30 rounded-[20px] flex flex-col items-center justify-center gap-2 text-center px-3 hover:border-primary/60 transition"
                      data-testid={`cat-plus-${cat.slug}`}
                    >
                      <div className="size-12 rounded-full bg-primary/15 border border-primary/40 flex items-center justify-center">
                        <Plus className="size-6 text-primary" />
                      </div>
                      <div className="text-sm font-bold text-primary">{t("categories.see_all")}</div>
                      <div className="text-[11px] text-muted-foreground">{total} {t("categories.n_shops")}</div>
                    </Link>
                  </AutoSlideStrip>
                </div>

                {/* Inline discounted products (mid-feed) */}
                {showPromosBelow && (
                  <section className="mt-8" data-testid="section-promos-inline">
                    <div className="rounded-[24px] overflow-hidden surface-elevated border border-rose-500/25 p-5 sm:p-6 relative">
                      <div className="pointer-events-none absolute -top-16 -end-16 size-48 rounded-full bg-rose-500/10 blur-3xl" />
                      <div className="relative">
                        <div className="flex items-start justify-between gap-4 mb-4">
                          <div className="min-w-0 flex-1">
                            <h2 className="text-xl sm:text-2xl md:text-3xl font-black leading-tight">
                              التخفيضات والعروض
                            </h2>
                          </div>
                          <div className="shrink-0 text-end">
                            <div className="text-xs font-bold text-rose-400 flex items-center gap-1.5 justify-end uppercase tracking-wider">
                              <Sparkles className="size-4" /> عروض المحلات
                            </div>
                          </div>
                        </div>
                        <DiscountedProductsCarousel products={discountedProducts} categoryLabelMap={Object.fromEntries(cats.map(c => [c.slug, c.name_ar || c.name || c.slug]))} />
                      </div>
                    </div>
                  </section>
                )}
              </div>
            );
          })}
        </section>
      )}

      {/* Fallback: if no shops yet, still show discounted products */}
      {shopsByCategory.length === 0 && discountedProducts.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-12" data-testid="section-promos">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="text-sm font-semibold text-rose-400 mb-1 flex items-center gap-1"><Sparkles className="size-4" /> عروض المحلات</div>
              <h2 className="text-2xl sm:text-3xl font-black">التخفيضات والعروض</h2>
            </div>
          </div>
          <DiscountedProductsCarousel products={discountedProducts} categoryLabelMap={Object.fromEntries(cats.map(c => [c.slug, c.name_ar || c.name || c.slug]))} />
        </section>
      )}

      {/* Install App section + business owner CTA moved to the drawer menu */}

      {/* Taxi request sheets */}
      <TaxiRequestSheet type="internal" open={showTaxiInternal} onOpenChange={setShowTaxiInternal} />
      <TaxiRequestSheet type="external" open={showTaxiExternal} onOpenChange={setShowTaxiExternal} />
    </div>
  );
}
